import React from "react";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsChange?: React.Dispatch<React.SetStateAction<number>>;
  pageSizeOptions?: number[];
  compact?: boolean;
}

const Pagination = (props: PaginationProps) => {
  const { currentPage, totalItems, itemsPerPage, onPageChange, onRowsChange } =
    props;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const maxPagesToShow = 5;

  // Calculate page numbers to show
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div
      className={
        props.compact
          ? `flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-gray-900/90 border-t border-[#d0d4e4] dark:border-[#394564] rounded-bl-lg rounded-br-lg`
          : `grid grid-cols-1 md:grid-cols-3 items-center px-4 py-3 bg-white dark:bg-[#121419] border-t border-[#d0d4e4] dark:border-[#394564] rounded-bl-lg rounded-br-lg`
      }
    >
      <div className="flex items-center gap-2 text-sm shrink-0 flex-wrap">
        <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">
          Page Per Sheet:
        </span>

        <div className="relative">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              onRowsChange?.(Number(e.target.value));
              onPageChange(1);
            }}
            className="
            appearance-none bg-white dark:bg-[#1E1E2D]
            border border-gray-600 rounded-md
            px-3 py-1 pr-8 text-sm
            text-[#3D3D3D] dark:text-white
            font-figtree
            focus:outline-none focus:ring-1 focus:ring-blue-500
            cursor-pointer
          "
          >
            {(props.pageSizeOptions || [10, 15, 20, 50]).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {props.compact && (
          <span className="text-gray-600 dark:text-gray-400 ml-2 whitespace-nowrap">
            {itemsPerPage} of {totalItems} stocks
          </span>
        )}
      </div>

      {!props.compact && (
        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
          Showing{" "}
          <span className="font-medium">
            {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}
          </span>{" "}
          to{" "}
          <span className="font-medium">
            {Math.min(currentPage * itemsPerPage, totalItems)}
          </span>{" "}
          of <span className="font-medium">{totalItems}</span> results
        </div>
      )}
      <div className="flex justify-end shrink-0">
        {totalPages > 1 ? (
          <div className="flex space-x-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md ${
                currentPage === 1
                  ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              }`}
            >
              Previous
            </button>

            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`w-8 h-8 rounded-md cursor-pointer ${
                  currentPage === pageNum
                    ? "bg-gradient-to-t from-[#066766] to-[#0FA243] text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md ${
                currentPage === totalPages
                  ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              }`}
            >
              Next
            </button>
          </div>
        ) : (
          <div className="h-8"></div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
